import { Link } from "@/components/LocalizedLink";
import { formatSek } from "@/lib/price-format";
import { useEffect, useState } from "react";
import { ArrowRight, Check } from "lucide-react";
import { track } from "@/lib/analytics";
import { isNativeAppleAvailable } from "@/lib/apple-iap";
import { useTranslation } from "react-i18next";

type PlanId = "premium_10d_once" | "premium_30d_once_v2" | "premium_90d_once";

const PLANS: {
  id: PlanId;
  price: number;
  days: number;
  labelKey: string;
  noteKey: string;
  badgeKey?: string;
  saveKey?: string;
}[] = [
  { id: "premium_10d_once", price: 199, days: 10, labelKey: "planPicker.label10", noteKey: "planPicker.note10" },
  {
    id: "premium_30d_once_v2",
    price: 349,
    days: 30,
    labelKey: "planPicker.label30",
    noteKey: "planPicker.note30",
    badgeKey: "planPicker.badgeRecommended",
    saveKey: "planPicker.save30",
  },
  {
    id: "premium_90d_once",
    price: 599,
    days: 90,
    labelKey: "planPicker.label90",
    noteKey: "planPicker.note90",
    badgeKey: "planPicker.badgeBest",
    saveKey: "planPicker.save90",
  },
];

const perDayAmount = (price: number, days: number) =>
  (price / days).toFixed(1).replace(".", ",");


/**
 * Kompakt tre-valsblock som ersätter enkel-CTA:er på hög-intent-ytor
 * (resultat, låst provsimulering, övningsgräns). Renderas aldrig för
 * premiumkunder – anropande yta villkorar på `premium === false`.
 * Döljs helt i native Apple-läge (App Store-regler).
 */
export function PlanPicker({
  source,
  className = "",
  tone = "light",
  compactMobile = false,
}: {
  source: string;
  className?: string;
  tone?: "light" | "dark";
  compactMobile?: boolean;
}) {
  const { t } = useTranslation();
  const [selected, setSelected] = useState<PlanId>("premium_30d_once_v2");
  const [appleMode, setAppleMode] = useState(false);
  useEffect(() => setAppleMode(isNativeAppleAvailable()), []);

  useEffect(() => {
    if (appleMode) return;
    track("Plan Picker Shown", { source });
  }, [appleMode, source]);

  if (appleMode) return null;

  const dark = tone === "dark";
  const sel = PLANS.find((p) => p.id === selected)!;

  return (
    <>
    {compactMobile && (
      <div data-purchase-cta data-android-hide className={`sm:hidden ${className}`}>
        <div role="radiogroup" aria-label={t("cPricing.mobileAria")} className="flex flex-col gap-2">
          {PLANS.map((p) => {
            const active = selected === p.id;
            return (
              <button
                key={p.id}
                type="button"
                role="radio"
                aria-checked={active}
                onClick={() => {
                  setSelected(p.id);
                  track("Mobile Plan Selected", { plan: p.id, source });
                }}
                className={`relative grid grid-cols-[auto_minmax(0,1fr)_auto] items-center gap-3 rounded-xl border-2 bg-card px-3.5 py-2.5 text-left text-foreground transition ${
                  active ? "border-primary shadow-elevated" : "border-border"
                }`}
              >
                {p.badgeKey && (
                  <span
                    className={`absolute -top-2 left-3 rounded-full px-2 py-0.5 text-[10px] font-bold uppercase tracking-wide ${
                      p.id === "premium_30d_once_v2" ? "bg-primary text-primary-foreground" : "bg-accent text-accent-foreground"
                    }`}
                  >
                    ★ {t(p.badgeKey)}
                  </span>
                )}
                <span className={`grid h-5 w-5 place-items-center rounded-full border-2 ${active ? "border-primary" : "border-muted-foreground/40"}`}>
                  {active && <span className="h-2.5 w-2.5 rounded-full bg-primary" />}
                </span>
                <span className="min-w-0">
                  <span className="block font-display text-sm font-bold">{t(p.labelKey)}</span>
                  <span className="block truncate text-[11px] text-muted-foreground">{t(p.noteKey)}</span>
                </span>
                <span className="text-right">
                  <span className="block font-display text-base font-black text-primary">{formatSek(p.price)}</span>
                  <span className="block text-[10px] text-muted-foreground">
                    {t("planPicker.perDay", { amount: perDayAmount(p.price, p.days) })}
                  </span>
                </span>
              </button>
            );
          })}
        </div>
        <Link
          to="/checkout"
          search={{ plan: sel.id } as any}
          onClick={() => {
            track("Plan Picker Checkout", { source, plan: sel.id, price: sel.price });
            track("Upgrade Block Clicked", { source, plan: sel.id });
          }}
          className="mt-3 inline-flex w-full items-center justify-center gap-2 rounded-xl bg-primary px-4 py-3 text-sm font-bold text-primary-foreground shadow-lg shadow-primary/20"
        >
          {t("cPricing.mobileCta", { days: sel.days, price: formatSek(sel.price) })}
          <ArrowRight className="h-4 w-4" />
        </Link>
      </div>
    )}
    <div data-purchase-cta data-android-hide className={`${compactMobile ? "hidden sm:grid" : "grid"} gap-3 sm:grid-cols-3 ${className}`}>
      {PLANS.map((p) => {
        const recommended = p.id === "premium_30d_once_v2";
        const highlight = p.id === "premium_90d_once" || recommended;
        return (
          <Link
            key={p.id}
            to="/checkout"
            search={{ plan: p.id } as any}
            onClick={() => {
              track("Plan Picker Checkout", { source, plan: p.id, price: p.price });
              track("Upgrade Block Clicked", { source, plan: p.id });
            }}
            className={[
              "group relative flex flex-col rounded-2xl border p-4 text-left transition",
              recommended
                ? "border-primary bg-primary/10 shadow-elevated ring-2 ring-primary/30"
                : p.id === "premium_90d_once"
                  ? "border-accent bg-accent/10 shadow-elevated"
                  : dark
                    ? "border-primary-foreground/20 bg-primary-foreground/5 hover:bg-primary-foreground/10"
                    : "border-border bg-card hover:bg-secondary/60",
            ].join(" ")}
          >
            {p.badgeKey && (
              <span
                className={`absolute -top-2 right-3 rounded-full px-2 py-0.5 text-[10px] font-semibold ${
                  recommended
                    ? "bg-primary text-primary-foreground"
                    : "bg-accent text-accent-foreground"
                }`}
              >
                {t(p.badgeKey)}
              </span>
            )}
            <span className={`text-xs font-medium ${dark && !highlight ? "text-primary-foreground/70" : "text-muted-foreground"}`}>
              {t(p.labelKey)}
            </span>
            <span className={`mt-1 font-display text-2xl font-bold ${dark && !highlight ? "text-primary-foreground" : ""}`}>
              {formatSek(p.price)}
            </span>
            <span className={`text-xs ${dark && !highlight ? "text-primary-foreground/70" : "text-muted-foreground"}`}>
              {t("planPicker.perDay", { amount: perDayAmount(p.price, p.days) })}
              {p.saveKey && <span className="ml-1 font-semibold text-success">· {t(p.saveKey)}</span>}
            </span>
            <span
              className={`mt-1 flex items-start gap-1.5 text-xs ${
                p.id === "premium_90d_once"
                  ? "font-medium text-accent"
                  : recommended
                    ? "font-medium text-primary"
                    : dark
                      ? "text-primary-foreground/70"
                      : "text-muted-foreground"
              }`}
            >
              {highlight && <Check className="mt-0.5 h-3.5 w-3.5 shrink-0" />}
              {t(p.noteKey)}
            </span>

            <span
              className={`mt-3 inline-flex items-center gap-1 text-sm font-semibold ${
                p.id === "premium_90d_once"
                  ? "text-accent"
                  : recommended
                    ? "text-primary"
                    : dark
                      ? "text-primary-foreground"
                      : "text-primary"
              }`}
            >

              {t("planPicker.choose")} <ArrowRight className="h-3.5 w-3.5 transition group-hover:translate-x-0.5" />
            </span>
          </Link>
        );
      })}
    </div>
    </>
  );
}
